import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Button, Popconfirm, Spin, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import moment from 'moment';
import styled from 'styled-components';

const { Text } = Typography;

import { useCreatePermissions } from '@hooks/usePermissions';
import { useGetBlockEvents, usePostUnblockEvents } from '@api/emp/queryHooks';
import { generateSelectOptions } from '@src/emp-3.0/utils/buildOptions';
import { BlockedDetailPanel } from '@components/detail-panel';
import { blockedEventTypeOptions } from '@src/emp-3.0/constants/blockEvents';
import { getFilteredSortedData } from '@src/emp-3.0/utils/management';
import {
  BLOCK_EVENTS,
  dateFields,
  handleAction,
  supportSearchFields,
} from '@src/emp-3.0/constants/management';
import { IBlockEventData } from '@src/common/types/empTypes';
import { useBlockEventsManagementStore, managementTableStyles } from '..';
import BlockedEventDetail from './BlockedEventDetail';

import UnlockSvg from '@icons/unlock.svg';
import TableWithSearch from '../components/TableWithSearch';

export const DetailRowClass = styled.div`
  .no-padding-row > td {
    padding: 0 !important;
  }
`;

const BlockEvents = () => {
  const [activeEventId, setActiveEventId] = useState<string | null>(null);

  const hasCreate = useCreatePermissions();

  const unblockConfirmTitle = (
    <div
      style={{
        width: '404px',
        fontWeight: 500,
        letterSpacing: '-0.14px',
        lineHeight: '20px',
        color: '#333',
      }}
    >
      Unblock this item? Once unblocked, it will be removed from the Blocked Events and sensor data
      reporting will resume.
    </div>
  );

  const {
    activeBlockEventKey,
    selectedList,
    setActiveBlockEventKey,
    setSelectedList,

    // Filters
    showFilter,
    searchText,
    setSearchText,
    setShowFilter,
    type,
    blockedBy,
    blockedDate,
    setType,
    setBlockedBy,
    setBlockedDate,

    // Sorting
    sort,
    setSort,

    // Reset
    resetFilter,
    resetStore,
  } = useBlockEventsManagementStore((state) => state);

  const {
    data: blockEventsList,
    isLoading: isLoadingBlockEventsList,
    refetch: refetchBlockEventsList,
  } = useGetBlockEvents();

  const { mutate: unblockEvents, isLoading: isLoadingUnblockEvents } = usePostUnblockEvents();

  const isLoading = useMemo(
    () => isLoadingUnblockEvents || isLoadingBlockEventsList,
    [isLoadingBlockEventsList, isLoadingUnblockEvents],
  );

  useEffect(() => {
    refetchBlockEventsList();
  }, [refetchBlockEventsList]);

  const filteredSortedData = getFilteredSortedData<IBlockEventData>({
    dataList: blockEventsList?.data,
    filterParams: {
      type,
      blockedDate,
      blockedBy,
    },
    searchParams: {
      searchText,
      supportSearchFields,
      dateFields,
    },
    sortParams: sort,
  });

  const tableData = useMemo(
    () =>
      filteredSortedData.map((item) => ({
        id: item.event_id,
        ...item,
      })),
    [filteredSortedData],
  );

  const flattenedData = useMemo(() => {
    if (!activeEventId) return tableData;

    const result: any[] = [];

    tableData.forEach((row) => {
      result.push(row);
      if (row.event_id === activeEventId) {
        result.push({ isDetail: true, parent: row });
      }
    });

    return result;
  }, [tableData, activeEventId]);

  const handleUnblockEvent = useCallback(
    ({ rowData }: { rowData: any }) => {
      if (rowData) {
        handleAction({
          action: unblockEvents,
          params: { events: [rowData.event_name] },
          onSuccess: refetchBlockEventsList,
          successMessage: `${rowData.event_name} has been unblocked from sensor.`,
          errorMessage: 'Failed to unblock, please try again later!',
          showSensorDelayMessage: true,
        });
      }
    },
    [refetchBlockEventsList, unblockEvents],
  );

  const columns: ColumnsType<any> = [
    {
      title: 'Custom Event',
      dataIndex: 'event_name',
      width: '20%',
      render: (text, record) => {
        if (record.isDetail) return { children: null, props: { colSpan: 0 } };
        return (
          <a
            onClick={() => setActiveEventId(record.event_id)}
            id="customEventName"
            data-test-id={text}
          >
            {text}
          </a>
        );
      },
    },
    {
      title: 'Block Rule',
      width: '20%',
      dataIndex: 'block_rules',
      render: (rules, record) => {
        if (record.isDetail) return { children: null, props: { colSpan: 0 } };
        const ruleText = rules.length ? 'Block by Rules' : 'Block All';
        return (
          <span id="customEventBlockRule" data-test-id={`${record.event_name}-${ruleText}`}>
            {ruleText}
          </span>
        );
      },
    },
    {
      title: 'Blocked By',
      width: '30%',
      dataIndex: 'block_by',
      render: (text, record) => {
        if (record.isDetail) return { children: null, props: { colSpan: 0 } };
        return (
          <span id="customEventBlockedBy" data-test-id={`${record.event_name}-${text}`}>
            {text}
          </span>
        );
      },
    },
    {
      title: 'Blocked Date',
      width: '30%',
      dataIndex: 'block_timestamp',
      render: (ts, record) => {
        if (record.isDetail)
          return {
            children: (
              <BlockedEventDetail
                record={record.parent}
                onClose={() => setActiveEventId(null)}
                onUnblock={() => {
                  handleUnblockEvent({ rowData: record.parent });
                  setActiveEventId(null);
                }}
              />
            ),
            props: { colSpan: 5 },
            style: { background: '#fafafa' },
          };
        const dateText = moment.unix(ts).format('YYYY/MM/DD HH:mm');
        return (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span id="customEventBlockedDate" data-test-id={`${record.event_name}-${dateText}`}>
              {dateText}
            </span>
            {hasCreate ? (
              <Popconfirm
                title={unblockConfirmTitle}
                okText="Yes"
                cancelText="No"
                placement="topRight"
                onConfirm={() => handleUnblockEvent({ rowData: record })}
              >
                <Button
                  type="default"
                  id="unblockEventsButton"
                  data-test-id={`unblockButton-${record.event_name}`}
                  icon={<UnlockSvg style={{ marginRight: 8 }} />}
                  style={{
                    color: '#1677FF',
                    borderColor: '#1677FF',
                    display: 'inline-flex',
                    alignItems: 'center',
                    fontWeight: 500,
                  }}
                  className="custom-unblock-button"
                >
                  Unblock
                </Button>
              </Popconfirm>
            ) : null}
          </div>
        );
      },
    },
  ];

  return (
    <Spin spinning={isLoading}>
      <div style={{ padding: '8px 16px' }}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <span style={{ fontSize: '20px', fontWeight: '500' }} data-test-id="blockedEventsHeader">
            Blocked Events
          </span>
        </div>

        <div style={{ width: '95%', marginTop: 8 }}>
          <Text
            style={{ fontSize: '14px', color: '#8c8c8c', fontWeight: 400 ,width: '95%'}}
            data-test-id="blockedEventsDescription"
          >
            This feature allows you to efficiently restrict the collection of custom events.
            Limiting data ingestion to the most relevant and meaningful events from the sensor
            ensures accuracy and relevancy of your experience-centric data.
          </Text>
        </div>

        <DetailRowClass>
          <TableWithSearch
            data={flattenedData}
            columns={columns}
            rowKey={(record) => record.event_id || `${record.parent?.event_id}-detail`}
            pagination={true}
            searchKeys={['event_name', 'event_type', 'block_by']} // Define searchable keys based on your data shape
          />
        </DetailRowClass>
      </div>
    </Spin>
  );
};

export default BlockEvents;
